import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Button, List, ListItem, ListItemText,
  Divider, CircularProgress, Alert, LinearProgress, Tabs, Tab
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import FeedbackIcon from '@mui/icons-material/Feedback';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate } from 'react-router-dom';
import socketService from '../services/socketService';
import FeedbackReportGrid from './FeedbackReportGrid';
import { useAssignmentService } from '../services/assignmentService';
import { useFeedbackService, FeedbackType } from '../services/feedbackService';
import { stopGlobalAudio } from '../utils/streamAudioPlayer';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';



interface GradingViewProps {
  transcript: { speaker: string, text: string, timestamp: number}[];
  conversationId: string;
  assessmentId: string;
  assignmentId: number;
  assessmentTitle: string;
  dateCompleted: string;
}

const GradingView: React.FC<GradingViewProps> = ({
  transcript,
  conversationId,
  assessmentId,
  assignmentId,
  assessmentTitle,
  dateCompleted,
}) => {
  const [gradingStatus, setGradingStatus] = useState<string>('');
  const [gradingProgress, setGradingProgress] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [isGrading, setIsGrading] = useState(false);
  const [gradingStarted, setGradingStarted] = useState(false);
  const [resultId, setResultId] = useState<string | null>(null);
  const [transcriptId, setTranscriptId] = useState<number | null>(null);
  const [tabIndex, setTabIndex] = useState<number>(0);
  const { fetchSingleFeedback } = useFeedbackService();
  const [feedback, setFeedback] = useState<FeedbackType | null>(null);
  const [loadingFeedback, setLoadingFeedback] = useState(false);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);
  const [showPrompt, setShowPrompt] = useState(true);


  const navigate = useNavigate();
  const assignmentService = useAssignmentService();

  useEffect(() => {
    socketService.onGradingStarted((data) => {
      stopGlobalAudio();
      setIsGrading(true);
      setGradingStatus(data.status);
      setGradingProgress(10);
      setError(null);
    });

    socketService.onGradingProgress((data) => {
      setGradingStatus(data.status);
      setGradingProgress(prev => Math.min(prev + 20, 90));
    });

    socketService.onGradingComplete((data) => {
      setResultId(data.result_id);
      setTranscriptId(data.transcript_id);
      setGradingStatus('Grading completed successfully!');
      setGradingProgress(100);
      setIsGrading(false);
    });

    socketService.onGradingError((error) => {
      setError(error.message);
      setIsGrading(false);
      setGradingProgress(0);
    });

    if (transcript.length > 0 && !gradingStarted) {
      setGradingStarted(true);
      socketService.gradeTranscript(
        transcript,
        conversationId,
        assessmentId,
        dateCompleted,
        assignmentId,
      );
    }

    return () => {
      socketService.offAllListeners();
    };
  }, [transcript, conversationId, gradingStarted, assessmentId, dateCompleted]);

  useEffect(() => {
    const loadFeedback = async () => {
      if (resultId) {
        setLoadingFeedback(true);
        setFeedbackError(null);
        try {
          const fb = await fetchSingleFeedback(resultId);
          setFeedback(fb);
        } catch (err) {
          setFeedbackError(err instanceof Error ? err.message : 'Failed to load feedback');
        } finally {
          setLoadingFeedback(false);
        }
      }
    };
    loadFeedback();
  }, [resultId]);

  const formatTime = (totalSeconds: number) => {
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const handleRetry = () => {
    setError(null);
    setResultId(null);
    setGradingStatus('');
    setGradingProgress(0);
    socketService.gradeTranscript(
      transcript,
      conversationId,
      assessmentId,
      dateCompleted,
      assignmentId,
    );
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const handleDownloadTranscript = async (transcriptId: number) => {
    try {
      const response = await assignmentService.downloadTranscriptResponse(transcriptId);
      const disposition = response.headers.get("Content-Disposition");

      // Fail if no content-disposition or filename
      if (!disposition) {
        alert("No Content-Disposition header found. Cannot download transcript.");
        return;
      }

      // Regex for filename (handles quotes and non-quotes, as well as UTF-8 filenames)
      let filename: string | null = null;
      const filenameRegex = /filename\*?=(?:UTF-8'')?["']?([^"';\n]+)["']?/i;
      const match = disposition.match(filenameRegex);
      if (match && match[1]) {
        filename = decodeURIComponent(match[1]);
      }

      if (!filename) {
        alert("Could not determine transcript filename from server response.");
        return;
      }

      const blob = await response.blob();
      const url = globalThis.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      globalThis.URL.revokeObjectURL(url);
    } catch (err) {
      alert("Transcript download failed " + transcriptId);
    }
  };

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100%',
      p: 4,
      mx: 'auto',
      position: 'relative',
      width: '100%',
    }}>
      {/* Header with Back to Home */}
      <Box sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 2
      }}>
        <Typography variant="h4">
          Meeting Transcript Grading
        </Typography>
        
        <div>
          {!isNaN(transcriptId!) && transcriptId! > 0 && (
          <Button
          variant="outlined"
            onClick={() => handleDownloadTranscript(transcriptId!)}
            color="primary"
            sx={{ m: 0.5 }}
          >
            Download Transcript
          </Button>
        )}
          <Button
            variant="outlined"
            color="secondary"
            sx={{ m: 0.5 }}
            onClick={() => {
              const encodedTitle = encodeURIComponent(assessmentTitle);
              const url = `https://forms.office.com/Pages/ResponsePage.aspx?id=nLqEdZHdRUebfrTenaO-e__9WnyBWt9Pt0AYwlCk-adUMVhZNk1ERVhHTTExREFSRllRNlJSVU5FNy4u&rc8f0784852044b91984e38f572a20068=${encodedTitle}`;
              window.open(url, '_blank', 'noopener,noreferrer');
            }}
          >
            Give Feedback
          </Button>
          <Button
            variant="outlined"
            sx={{ m: 0.5 }}
            onClick={() => navigate('/')}
          >
            Back to Home
          </Button>
          </div>
      </Box>

      {/* Tabs */}
      <Tabs value={tabIndex} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab label="Grading & Results" />
        <Tab label="Transcript" />
      </Tabs>

      {/* Tab Panels */}
      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        {tabIndex === 0 && (
          <>
          {showPrompt && (
            <Dialog
              open={showPrompt}
              onClose={() => setShowPrompt(false)}
              // maxWidth="sm"
              fullWidth
              >
              <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                Feedback
                <IconButton
                  aria-label="dismiss feedback prompt"
                  onClick={() => setShowPrompt(false)}
                  size="small"
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<FeedbackIcon />}
                  endIcon={<OpenInNewIcon />}
                  size="large"
                  fullWidth
                  sx={{ mb: 1, justifyContent: "space-evenly" }}
                  onClick={() => {
                    const encodedTitle = encodeURIComponent(assessmentTitle);
                    const url = `https://forms.office.com/Pages/ResponsePage.aspx?id=nLqEdZHdRUebfrTenaO-e__9WnyBWt9Pt0AYwlCk-adUMVhZNk1ERVhHTTExREFSRllRNlJSVU5FNy4u&rc8f0784852044b91984e38f572a20068=${encodedTitle}`;
                    window.open(url, '_blank', 'noopener,noreferrer');
                    setShowPrompt(false);
                  }}
                >
                  <Box>Congratulations on completing your assessment! <br></br>
                  <strong>Please complete this survey.</strong></Box>
                  
                </Button>
                <Typography variant="body2" color="text.secondary">
                  Your feedback is important in improving our AI roleplay tool.
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setShowPrompt(false)} color="primary">
                  Close
                </Button>
              </DialogActions>
            </Dialog>
          )}
            {isGrading && (
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Grading in Progress
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CircularProgress size={24} sx={{ mr: 2 }} />
                  <Typography>{gradingStatus}</Typography>
                </Box>
                <LinearProgress variant="determinate" value={gradingProgress} />
              </Paper>
            )}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                <Typography variant="body1">{error}</Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleRetry}
                  sx={{ mt: 1 }}
                >
                  Retry Grading
                </Button>
              </Alert>
            )}
            {loadingFeedback && (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
              </Box>
            )}
            {feedbackError && (
              <Alert severity="error">{feedbackError}</Alert>
            )}
            {feedback && (
              <Box sx={{ mb: 2 }}>
                <FeedbackReportGrid feedback={feedback} />
              </Box>
            )}
            {!isGrading && !feedback && !loadingFeedback && !error && (
              <Typography color="text.secondary">
                No grading results available yet.
              </Typography>
            )}
          </>
        )}
        {tabIndex === 1 && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Transcript
            </Typography>
            <List sx={{ maxHeight: '60vh', overflow: 'auto' }}>
              {transcript.map((item, index) => (
                <React.Fragment key={index}>
                  <ListItem>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle2" color="primary">
                          {item.speaker} &nbsp;|&nbsp; {formatTime(item.timestamp)}
                        </Typography>
                      }
                      secondary={item.text}
                    />
                  </ListItem>
                  {index < transcript.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
            {transcript.length === 0 && (
              <Typography color="text.secondary">
                No transcript available.
              </Typography>
            )}
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default GradingView;